import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AuditFilters = ({ filters, onFiltersChange, onExport, isExporting }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const actionTypeOptions = [
    { value: '', label: 'All Actions' },
    { value: 'created', label: 'Created' },
    { value: 'modified', label: 'Modified' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'exported', label: 'Exported' },
    { value: 'deleted', label: 'Deleted' }
  ];

  const userOptions = [
    { value: '', label: 'All Users' },
    { value: 'sarah.johnson@company.com', label: 'Sarah Johnson' },
    { value: 'mike.chen@company.com', label: 'Mike Chen' },
    { value: 'emma.williams@company.com', label: 'Emma Williams' },
    { value: 'david.brown@company.com', label: 'David Brown' },
    { value: 'system', label: 'System' }
  ];

  const fieldOptions = [
    { value: '', label: 'All Fields' },
    { value: 'vendor_name', label: 'Vendor Name' },
    { value: 'invoice_number', label: 'Invoice Number' },
    { value: 'invoice_date', label: 'Invoice Date' },
    { value: 'total_amount', label: 'Total Amount' },
    { value: 'vat_amount', label: 'VAT Amount' },
    { value: 'vendor_reference', label: 'Vendor Reference' },
    { value: 'line_items', label: 'Line Items' },
    { value: 'status', label: 'Status' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      search: '',
      dateFrom: '',
      dateTo: '',
      actionType: '',
      user: '',
      field: '',
      invoiceReference: ''
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => value && value?.trim() !== '')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
        >
          <Icon 
            name={isExpanded ? "ChevronDown" : "ChevronRight"} 
            size={16} 
          />
          <span>Audit Filters</span>
          {getActiveFilterCount() > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </button>

        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            loading={isExporting}
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Filter Content */}
      {isExpanded && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Search */}
            <Input
              type="search"
              placeholder="Search audit entries..."
              value={filters?.search}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
              className="min-w-[200px]"
            />

            {/* Date Range */}
            <div className="flex items-center space-x-2">
              <Input
                type="date"
                label="From"
                value={filters?.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className="w-36"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                label="To"
                value={filters?.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className="w-36"
              />
            </div>

            {/* Action Type */}
            <Select
              label="Action Type"
              options={actionTypeOptions}
              value={filters?.actionType}
              onChange={(value) => handleFilterChange('actionType', value)}
              className="min-w-[160px]"
            />

            {/* User */}
            <Select
              label="User"
              options={userOptions}
              value={filters?.user}
              onChange={(value) => handleFilterChange('user', value)}
              searchable
              className="min-w-[160px]"
            />

            {/* Field */}
            <Select
              label="Field Changed"
              options={fieldOptions}
              value={filters?.field}
              onChange={(value) => handleFilterChange('field', value)}
              className="min-w-[160px]"
            />

            {/* Invoice Reference */}
            <Input
              type="text"
              label="Invoice Reference"
              placeholder="INV-2024-001"
              value={filters?.invoiceReference}
              onChange={(e) => handleFilterChange('invoiceReference', e?.target?.value)}
              className="min-w-[160px]"
            />
          </div>

          {/* Quick Actions */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('actionType', 'modified')}
              >
                Recent Changes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date()?.toISOString()?.split('T')?.[0];
                  handleFilterChange('dateFrom', today);
                  handleFilterChange('dateTo', today);
                }}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const weekAgo = new Date();
                  weekAgo?.setDate(weekAgo?.getDate() - 7);
                  handleFilterChange('dateFrom', weekAgo?.toISOString()?.split('T')?.[0]);
                  handleFilterChange('dateTo', new Date()?.toISOString()?.split('T')?.[0]);
                }}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('actionType', 'exported')}
              >
                Export Events
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFilters;